import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Modal } from '../ui/Modal.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { useToast } from '../ui/Toast.js';
import { useCreateReport } from '../../hooks/useReports.js';
import { Icon } from '../Icon.js';

type SelectableType = 'web_audit' | 'custom';

interface Props {
  leadId: string;
  leadName: string;
  initialUrl?: string | null;
  onClose: () => void;
  onCreated?: (reportId: number) => void;
}

const DISABLED_TYPES = ['seo_report', 'competitor_analysis'] as const;

function deriveTitle(type: SelectableType, leadName: string, t: TFunction): string {
  if (type === 'web_audit') return t('reports.types.webAuditWithLead', { leadName });
  return t('reports.types.customWithLead', { leadName });
}

export function ReportTypeSelector({ leadId, leadName, initialUrl, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const createReport = useCreateReport();

  const [selectedType, setSelectedType] = useState<SelectableType>('web_audit');
  const [url, setUrl] = useState(initialUrl ?? '');
  const [prompt, setPrompt] = useState('');
  const [urlError, setUrlError] = useState('');

  function validateAndSubmit() {
    if (selectedType === 'web_audit') {
      let normalizedUrl = url.trim();
      if (!normalizedUrl) { setUrlError(t('reports.errors.invalidUrl')); return; }
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = `https://${normalizedUrl}`;
      }
      try { new URL(normalizedUrl); } catch { setUrlError(t('reports.errors.invalidUrl')); return; }
      setUrlError('');
      createReport.mutate(
        { type: 'web_audit', title: deriveTitle('web_audit', leadName, t), inputs: { url: normalizedUrl }, leadId },
        {
          onSuccess: (data) => { onCreated?.(data.id); onClose(); },
          onError: (err) => toast.error(err instanceof Error ? err.message : 'Error'),
        },
      );
    } else {
      if (!prompt.trim()) return;
      createReport.mutate(
        { type: 'custom', title: deriveTitle('custom', leadName, t), inputs: { prompt: prompt.trim(), clientName: leadName }, leadId },
        {
          onSuccess: (data) => { onCreated?.(data.id); onClose(); },
          onError: (err) => toast.error(err instanceof Error ? err.message : 'Error'),
        },
      );
    }
  }

  const canSubmit = selectedType === 'web_audit' ? url.trim().length > 0 : prompt.trim().length > 0;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('reports.selector.title')}
      size="md"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button variant="ghost" tone="muted" size="sm" onClick={onClose} disabled={createReport.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="solid"
            tone="cyan"
            size="sm"
            disabled={!canSubmit || createReport.isPending}
            loading={createReport.isPending}
            onClick={validateAndSubmit}
          >
            {t('reports.selector.generateBtn')}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

        {/* Active types */}
        {(['web_audit', 'custom'] as SelectableType[]).map(type => {
          const isSelected = selectedType === type;
          const isCustom = type === 'custom';
          return (
            <div
              key={type}
              onClick={() => !createReport.isPending && setSelectedType(type)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px',
                padding: '14px 16px',
                border: `2px solid ${isSelected ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`,
                borderRadius: 'var(--evo-r-md)',
                background: isSelected ? 'rgba(0,212,255,0.06)' : 'var(--evo-bg-3)',
                cursor: createReport.isPending ? 'not-allowed' : 'pointer',
                opacity: createReport.isPending ? 0.6 : 1,
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <span style={{ color: isSelected ? 'var(--evo-cyan)' : 'var(--evo-fg-3)', display: 'flex', marginTop: '1px' }}>
                <Icon name={isCustom ? 'plus' : 'activity'} size={16} />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-list-title-fs)', fontWeight: 'var(--evo-fw-medium)', color: 'var(--evo-fg-1)' }}>
                  {isCustom ? t('reports.selector.customLabel') : t(`reports.types.${type}.label`)}
                </div>
                <div style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', marginTop: '2px' }}>
                  {t(`reports.types.${type}.description`)}
                </div>

                {/* Inline config fields for selected type */}
                {isSelected && type === 'web_audit' && (
                  <div style={{ marginTop: '12px' }} onClick={e => e.stopPropagation()}>
                    <Input
                      label={t('reports.selector.urlLabel')}
                      value={url}
                      onChange={val => { setUrl(val); setUrlError(''); }}
                      placeholder={t('reports.selector.urlPlaceholder')}
                      error={urlError || false}
                      onKeyDown={e => { if (e.key === 'Enter') validateAndSubmit(); }}
                    />
                    {!initialUrl && (
                      <p style={{ margin: '6px 0 0', fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
                        {t('reports.selector.noUrl')}
                      </p>
                    )}
                  </div>
                )}

                {isSelected && type === 'custom' && (
                  <div style={{ marginTop: '12px' }} onClick={e => e.stopPropagation()}>
                    <Textarea
                      label={t('reports.selector.promptLabel')}
                      value={prompt}
                      onChange={v => setPrompt(v)}
                      placeholder={t('reports.selector.promptPlaceholder')}
                      rows={4}
                      fullWidth
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Disabled / coming-soon types */}
        {DISABLED_TYPES.map(type => (
          <div
            key={type}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px',
              padding: '14px 16px',
              border: '1px solid var(--evo-stroke)',
              borderRadius: 'var(--evo-r-md)',
              background: 'var(--evo-bg-3)',
              opacity: 0.5,
              cursor: 'not-allowed',
            }}
          >
            <span style={{ color: 'var(--evo-fg-3)', display: 'flex', marginTop: '1px' }}>
              <Icon name="activity" size={16} />
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-list-title-fs)', fontWeight: 'var(--evo-fw-medium)', color: 'var(--evo-fg-3)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {type === 'seo_report' ? 'Auditoría SEO' : 'Análisis Competitivo'}
                <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: 'var(--evo-bg)', border: '1px solid var(--evo-stroke)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
                  {t('reports.selector.comingSoon')}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
